/**
 * Shared authenticated fetch utilities for React Query hooks.
 * All modules should use fetchApi (or fetchApiWithErrors for structured error responses).
 */

/**
 * Structured API error with code and error details.
 * Used by modules whose backends return { code, errors[] } on failure (e.g. SecretManager).
 */
export class ApiError extends Error {
  code: string;
  errors: Array<{ code: string; message: string; source?: string }>;

  constructor(code: string, errors: Array<{ code: string; message: string; source?: string }>) {
    super(errors.map(e => e.message).join(', ') || `API error: ${code}`);
    this.name = 'ApiError';
    this.code = code;
    this.errors = errors;
  }
}

/**
 * Appends `?cluster=` (or `&cluster=` if the path already has a query string)
 * so cluster-aware endpoints can be called against a single backend instance.
 */
export function withCluster(path: string, cluster: string | undefined): string {
  if (!cluster) return path;
  const sep = path.includes('?') ? '&' : '?';
  return `${path}${sep}cluster=${encodeURIComponent(cluster)}`;
}

/**
 * Standard authenticated fetch. Throws a generic Error on non-OK responses.
 * Suitable for most modules (Dashboard, MicroserviceGenerator).
 */
export async function fetchApi<T = any>(
  url: string,
  getToken: () => Promise<string>,
  options?: RequestInit,
): Promise<T> {
  const token = await getToken();
  const response = await fetch(url, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...options?.headers,
    },
  });

  if (!response.ok) {
    // Backends here return { error: "<message>" } on failure — surface it instead of a bare
    // status code, so callers (e.g. PR resolution) can show devs why a request actually failed.
    let message = `API request failed: ${response.status} ${response.statusText}`;
    try {
      const body = await response.clone().json();
      if (body?.error) message = body.error;
    } catch {
      // non-JSON body — keep the generic status message
    }
    const err = new Error(message) as Error & { status?: number };
    err.status = response.status;
    throw err;
  }

  return response.json();
}

/**
 * Authenticated fetch with structured error parsing.
 * On failure, parses the response body as { code, errors[] } and throws an ApiError.
 * Suitable for modules whose backends return structured error responses (e.g. SecretManager).
 */
export async function fetchApiWithErrors<T = any>(
  url: string,
  getToken: () => Promise<string>,
  options?: RequestInit,
): Promise<T> {
  const token = await getToken();
  const response = await fetch(url, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      ...(options?.body && { 'Content-Type': 'application/json' }),
      ...options?.headers,
    },
    mode: 'cors',
  });

  let content: any;
  if (response.headers.get('content-type')?.includes('application/json')) {
    content = await response.json();
  }

  if (!response.ok) {
    throw new ApiError(
      content?.code || 'unknown',
      content?.errors || [{ code: 'Unknown', message: `Request failed: ${response.status}` }],
    );
  }

  return content;
}
